import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { formatDistanceToNow } from 'date-fns';
import { CustomIcon } from '../UI';
import { useNotificationsContext } from '../../notifications/NotificationsProvider';
import PaymentEditModal from '../../pages/payments-page/components/PaymentEditModal';
import paymentService from '../../services/paymentService';
import LoadInfoModal from '../modals/load-info-modal/LoadInfoModal';
import loadService from '../../services/loadService';
import Loading from '../loading-animation';
import './NotificationsSidebar.scss';

const NotificationsSidebar = ({ isOpen, onClose }) => {
  const {
    notifications,
    activities,
    loading,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    refresh,
    connectionStatus
  } = useNotificationsContext();
  
  const lastRefreshRef = useRef(0);
  
  // Modal states
  const [paymentEditModal, setPaymentEditModal] = useState({
    isOpen: false,
    paymentData: null,
    paymentType: null,
    paymentId: null
  });
  
  const [infoModal, setInfoModal] = useState({
    isOpen: false,
    notification: null
  });

  const [loadInfoModal, setLoadInfoModal] = useState({
    isOpen: false,
    loadData: null
  });
  
  const [loadingPayment, setLoadingPayment] = useState(false);
  const [loadingLoad, setLoadingLoad] = useState(false);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      
      // Refresh notifications when sidebar opens, but debounce
      const now = Date.now();
      if (now - lastRefreshRef.current > 5000) { // Minimum 5 seconds between refreshes
        lastRefreshRef.current = now;
        refresh();
      }
    } else {
      document.body.style.overflow = '';
    }

    return () => {
      document.body.style.overflow = '';
    };
  }, [isOpen]); // Remove refresh from dependencies - it's stable now but we use ref for debounce

  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      return () => {
        document.removeEventListener('keydown', handleEscape);
      };
    }
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  // Format timestamp
  const formatTimestamp = (date) => {
    if (!date) return 'Just now';
    try {
      const dateObj = new Date(date);
      if (isNaN(dateObj.getTime())) return 'Just now';
      return formatDistanceToNow(dateObj, { addSuffix: true });
    } catch {
      return 'Just now';
    }
  };

  // Get notification icon based on type
  const getNotificationIcon = (type) => {
    switch (type) {
      case 'load_created':
      case 'load_updated':
      case 'load.created':
      case 'load.updated':
      case 'load.status_updated':
      case 'load_status_update':
      case 'load_status_changed':
      case 'load_assigned':
      case 'load_delivered':
        return 'trucka';
      case 'payment_receivable_created':
      case 'payment_receivable_status_update':
      case 'payment_payable_created':
      case 'payment_payable_status_update':
      case 'payment_status_changed':
      case 'payment_overdue':
      case 'payment_payable_need_to_pay':
      case 'payment.created':
      case 'payment.updated':
        return 'carda';
      case 'user_created':
      case 'user_updated':
      case 'carrier_created':
      case 'carrier_updated':
      case 'customer_created':
      case 'customer_updated':
        return 'person';
      default:
        return 'activity';
    }
  };

  // Check if notification is payment type
  const isPaymentNotification = (notification) => {
    const paymentTypes = [
      'payment_receivable_created',
      'payment_payable_created',
      'payment_receivable_status_update',
      'payment_payable_status_update',
      'payment_status_changed',
      'payment_overdue',
      'payment_payable_need_to_pay',
      'payment.created',
      'payment.updated'
    ];
    return paymentTypes.includes(notification.type);
  };

  // Check if notification is load type
  const isLoadNotification = (notification) => {
    const loadTypes = [
      'load_created',
      'load_updated',
      'load.created',
      'load.updated',
      'load.status_updated',
      'load_status_update',
      'load_status_changed',
      'load_assigned',
      'load_delivered'
    ];
    return loadTypes.includes(notification.type);
  };

  // Check if notification is activity type (user, carrier, customer)
  const isActivityNotification = (notification) => {
    const activityTypes = [
      'user_created',
      'user_updated',
      'carrier_created',
      'carrier_updated',
      'customer_created',
      'customer_updated'
    ];
    return activityTypes.includes(notification.type);
  };

  const getLoadStatus = (notification) => {
    const statusChange = notification.data?.changes?.find(c => c.field === 'status');
    return statusChange?.to ||
           notification.data?.newStatus ||
           notification.data?.load?.status ||
           notification.data?.status ||
           notification.data?.entity?.status ||
           null;
  };

  const getLoadStatusClass = (status) => {
    if (!status) return 'status-default';
    switch (status.toLowerCase()) {
      case 'delivered':
        return 'status-delivered';
      case 'pending':
        return 'status-pending';
      case 'cancelled':
        return 'status-cancelled';
      default:
        return 'status-default';
    }
  };

  // Categorize notifications
  const categorizeNotifications = (notifications) => {
    const loadNotifications = [];
    const paymentNotifications = [];
    const activityNotifications = [];

    notifications.forEach(notification => {
      if (isLoadNotification(notification)) {
        loadNotifications.push(notification);
      } else if (isPaymentNotification(notification)) {
        paymentNotifications.push(notification);
      } else if (isActivityNotification(notification)) {
        activityNotifications.push(notification);
      }
      // Other notifications are ignored (not shown)
    });

    return { loadNotifications, paymentNotifications, activityNotifications };
  };

  // Get payment type (payable or receivable) from notification
  const getPaymentType = (notification) => {
    const type = notification.type || '';
    
    // Check notification type first
    if (type.includes('receivable')) return 'receivable';
    if (type.includes('payable')) return 'payable';
    
    // Check data.type field (for payment.created events)
    const dataType = notification.data?.type || notification.data?.entity?.type;
    if (dataType === 'receivable' || dataType === 'payable') {
      return dataType;
    }
    
    // Check explicit paymentType field
    const paymentType = notification.data?.paymentType?.toLowerCase();
    if (paymentType === 'payable' || paymentType === 'receivable') {
      return paymentType;
    }
    
    // Check payment object if it exists
    if (notification.data?.payment?.type) {
      const paymentTypeFromData = notification.data.payment.type.toLowerCase();
      if (paymentTypeFromData === 'payable' || paymentTypeFromData === 'receivable') {
        return paymentTypeFromData;
      }
    }
    
    // Try to infer from message or other fields
    const message = (notification.message || '').toLowerCase();
    if (message.includes('receivable')) return 'receivable';
    if (message.includes('payable')) return 'payable';
    
    return null;
  };

  // Format notification content (title + optional detail)
  const getNotificationContent = (notification) => {
    const { type, data } = notification;

    // Helper to get orderId from various locations
    const getOrderId = () => {
      return data?.load?.orderId ||
             data?.orderId ||
             data?.meta?.orderId ||
             data?.entity?.orderId ||
             'N/A';
    };

    const orderId = getOrderId();
    const isLoadType = isLoadNotification(notification);
    const appendOrderId = (text) => {
      if (!isLoadType) return text;
      if (!orderId || orderId === 'N/A') return text;
      if (text.includes(orderId)) return text;
      return `${text} • ${orderId}`;
    };

    const generateFallbackText = () => {
      const safeOrderId = orderId;

      // Helper to get status from various locations
      const getStatus = () => {
        return data?.newStatus ||
               data?.load?.status ||
               data?.status ||
               data?.entity?.status ||
               'Updated';
      };

      switch (type) {
      case 'load.created':
      case 'load_created':
        return `New load created: ${safeOrderId}`;
      case 'load.updated':
      case 'load_updated':
        return `Load updated: ${safeOrderId}`;
      case 'load.status_updated':
      case 'load_status_update':
      case 'load_status_changed': {
        // Try to get status change from changes array
        const statusChange = data?.changes?.find(c => c.field === 'status');
        const oldStatus = statusChange?.from || data?.oldStatus || data?.meta?.oldStatus || 'N/A';
        const newStatus = statusChange?.to || getStatus();
        return `Load ${safeOrderId} status changed: ${oldStatus} → ${newStatus}`;
      }
      case 'load_delivered':
        return `Load delivered: ${safeOrderId}`;
      case 'load_assigned':
        return `Load assigned: ${safeOrderId}`;
      case 'payment_receivable_created':
      case 'payment.created': {
        // Check if it's a receivable payment
        const paymentType = data?.type || data?.payment?.type || data?.entity?.type;
        const paymentOrderId = safeOrderId;
        if (paymentType === 'receivable' || notification.type === 'payment_receivable_created') {
          return `Payment Receivable Created: ${paymentOrderId}`;
        } else if (paymentType === 'payable' || notification.type === 'payment_payable_created') {
          return `Payment Payable Created: ${paymentOrderId}`;
        }
        return `Payment Created: ${paymentOrderId}`;
      }
      case 'payment_payable_created': {
        const paymentOrderId = safeOrderId;
        return `Payment Payable Created: ${paymentOrderId}`;
      }
      case 'payment_receivable_status_update':
      case 'payment.updated': {
        const status = getStatus();
        const paymentOrderId = safeOrderId;
        return `Receivable ${paymentOrderId} status: ${status}`;
      }
      case 'payment_payable_status_update': {
        const status = getStatus();
        const paymentOrderId = safeOrderId;
        return `Payable ${paymentOrderId} status: ${status}`;
      }
      case 'payment_status_changed': {
        const paymentType = data?.paymentType || 'Payment';
        const status = getStatus();
        return `${paymentType} status: ${status}`;
      }
      case 'payment_overdue': {
        const paymentOrderId = safeOrderId;
        const paymentType = data?.paymentType || data?.type || data?.payment?.type;
        const label = paymentType === 'payable' ? 'Payment Payable Overdue' : 'Payment Overdue';
        return `⚠️ ${label}: ${paymentOrderId}`;
      }
      case 'payment_payable_need_to_pay': {
        const paymentOrderId = safeOrderId;
        return `💰 Payment Payable Due: ${paymentOrderId}`;
      }
      case 'user.created':
      case 'user_created': {
        const user = data?.user || data?.entity || {};
        const name = user.firstName || user.email || data?.email || 'User';
        return `User created: ${name}`;
      }
      case 'user.updated':
      case 'user_updated': {
        const user = data?.user || data?.entity || {};
        const name = user.firstName || user.email || data?.email || 'User';
        return `User updated: ${name}`;
      }
      case 'carrier.created':
      case 'carrier_created': {
        const carrier = data?.carrier || data?.entity || {};
        const name = carrier.companyName || carrier.name || data?.companyName || 'Carrier';
        return `Carrier created: ${name}`;
      }
      case 'carrier.updated':
      case 'carrier_updated': {
        const carrier = data?.carrier || data?.entity || {};
        const name = carrier.companyName || carrier.name || data?.companyName || 'Carrier';
        return `Carrier updated: ${name}`;
      }
      case 'customer.created':
      case 'customer_created': {
        const customer = data?.customer || data?.entity || {};
        const name = customer.companyName || data?.companyName || 'Customer';
        return `Customer created: ${name}`;
      }
      case 'customer.updated':
      case 'customer_updated': {
        const customer = data?.customer || data?.entity || {};
        const name = customer.companyName || data?.companyName || 'Customer';
        return `Customer updated: ${name}`;
      }
      default:
        return notification.title || notification.message || 'New notification';
      }
    };

    const title = notification.title ? appendOrderId(notification.title) : '';
    const message = notification.message ? appendOrderId(notification.message) : '';

    if (title) {
      return {
        title,
        detail: message && message !== title ? message : ''
      };
    }

    if (message) {
      return { title: message, detail: '' };
    }

    return { title: appendOrderId(generateFallbackText()), detail: '' };
  };

  // Handle payment notification click - open edit modal
  const handlePaymentNotificationClick = async (notification) => {
    if (!notification.read) {
      await markAsRead(notification._id || notification.id);
    }

    // Try to get payment ID from various possible locations
    const paymentId = notification.data?.paymentReceivableId || 
                      notification.data?.paymentPayableId ||
                      notification.data?.paymentId || 
                      notification.data?.payment?.id || 
                      notification.data?.payment?._id ||
                      notification.data?.id ||
                      notification.data?.entityId ||
                      notification.entityId;
    
    // Determine payment type from notification type or data
    const paymentType =
      getPaymentType(notification) ||
      notification.data?.paymentType ||
      notification.data?.meta?.paymentType ||
      notification.data?.payment?.type ||
      notification.data?.type ||
      'receivable';
    
    if (!paymentId) {
      console.error('Payment ID not found in notification data', notification);
      // Still open modal but with limited data
      setPaymentEditModal({
        isOpen: true,
        paymentData: notification.data?.payment || notification.data,
        paymentType: paymentType,
        paymentId: null
      });
      return;
    }

    setLoadingPayment(true);
    try {
      let paymentData = null;
  
      try {
        if (paymentType === 'receivable') {
          const response = await paymentService.getReceivableById(paymentId);
          paymentData = response?.data?.data || response?.data || response;
        } else {
          const response = await paymentService.getPayableById(paymentId);
          paymentData = response?.data?.data || response?.data || response;
        }
      } catch (err) {
        console.error('Error fetching payment:', err);
        paymentData = {
          ...notification.data,
          _id: paymentId,
          id: paymentId,
          amount: notification.data?.amount,
          status: notification.data?.newStatus || notification.data?.status,
          load: notification.data?.load,
        };
      }

      setPaymentEditModal({
        isOpen: true,
        paymentData: paymentData,
        paymentType: paymentType,
        paymentId: paymentId
      });
    } catch (error) {
      console.error('Error loading payment:', error);
      // Still try to open with available data
      setPaymentEditModal({
        isOpen: true,
        paymentData: {
          ...notification.data,
          _id: paymentId,
          id: paymentId
        },
        paymentType: paymentType,
        paymentId: paymentId
      });
    } finally {
      setLoadingPayment(false);
    }
  };

  // Handle load notification click - open LoadInfoModal
  const handleLoadNotificationClick = async (notification) => {
    if (!notification.read) {
      await markAsRead(notification._id || notification.id);
    }

    // Try to get load data from notification
    let loadData = notification.data?.load || notification.data?.entity;
    const loadId = notification.data?.entityId || 
                   notification.entityId || 
                   loadData?._id || 
                   loadData?.id;

    // If we have load data in notification, use it directly
    if (loadData && loadData._id) {
      setLoadInfoModal({
        isOpen: true,
        loadData: loadData
      });
      return;
    }

    // Otherwise, fetch load by ID
    if (loadId) {
      setLoadingLoad(true);
      try {
        const response = await loadService.getLoadById(loadId);
        const load = response?.data?.data || response?.data || response;
        
        if (load) {
          setLoadInfoModal({
            isOpen: true,
            loadData: load
          });
        } else {
          // Fallback: try to use data from notification even if incomplete
          if (loadData) {
            setLoadInfoModal({
              isOpen: true,
              loadData: { ...loadData, _id: loadId, id: loadId }
            });
          } else {
            console.error('Load not found:', loadId);
            // Show info modal as fallback
            setInfoModal({
              isOpen: true,
              notification: notification
            });
          }
        }
      } catch (error) {
        console.error('Error fetching load:', error);
        // Fallback: use notification data if available
        if (loadData) {
          setLoadInfoModal({
            isOpen: true,
            loadData: { ...loadData, _id: loadId, id: loadId }
          });
        } else {
          setInfoModal({
            isOpen: true,
            notification: notification
          });
        }
      } finally {
        setLoadingLoad(false);
      }
    } else {
      // No load ID, show info modal
      setInfoModal({
        isOpen: true,
        notification: notification
      });
    }
  };

  // Handle other notification click - open info modal
  const handleInfoNotificationClick = async (notification) => {
    if (!notification.read) {
      await markAsRead(notification._id || notification.id);
    }
    setInfoModal({
      isOpen: true,
      notification: notification
    });
  };

  // Handle notification click
  const handleNotificationClick = async (notification) => {
    if (isPaymentNotification(notification)) {
      await handlePaymentNotificationClick(notification);
    } else if (isLoadNotification(notification)) {
      await handleLoadNotificationClick(notification);
    } else {
      await handleInfoNotificationClick(notification);
    }
  };

  // Close payment edit modal
  const handleClosePaymentModal = () => {
    setPaymentEditModal({
      isOpen: false,
      paymentData: null,
      paymentType: null,
      paymentId: null
    });
    refresh(); // Refresh notifications after edit
  };

  // Close info modal
  const handleCloseInfoModal = () => {
    setInfoModal({
      isOpen: false,
      notification: null
    });
  };

  // Close load info modal
  const handleCloseLoadInfoModal = () => {
    setLoadInfoModal({
      isOpen: false,
      loadData: null
    });
  };

  // Sort notifications (unread first, then by date)
  const sortNotifications = (notifs) => {
    return [...notifs].sort((a, b) => {
      // Unread first
      if (a.read !== b.read) return a.read ? 1 : -1;
      // Then by date
      return new Date(b.createdAt || b.created_at) - new Date(a.createdAt || a.created_at);
    });
  };

  // Categorize and sort notifications
  const { loadNotifications, paymentNotifications, activityNotifications } = categorizeNotifications(notifications);
  const sortedLoadNotifications = sortNotifications(loadNotifications).slice(0, 10);
  const sortedPaymentNotifications = sortNotifications(paymentNotifications).slice(0, 10);
  const sortedActivityNotifications = sortNotifications(activityNotifications).slice(0, 10);

  // Combine activity notifications with activities from context (if any)
  const displayActivities = [...sortedActivityNotifications, ...activities.filter(activity => isActivityNotification(activity))].slice(0, 10);

  return (
    <div className="notifications-sidebar notifications-sidebar--open">
      {/* Overlay */}
      <div className="notifications-sidebar__overlay" onClick={onClose}></div>

      {/* Sidebar Content */}
      <div className="notifications-sidebar__content">
        {/* Connection Status Indicator */}
        <div className={`notifications-sidebar__connection-status notifications-sidebar__connection-status--${connectionStatus}`}>
          <div className="notifications-sidebar__connection-status-dot"></div>
          <span className="notifications-sidebar__connection-status-text">
            {connectionStatus === 'connected' && 'Connected'}
            {connectionStatus === 'reconnecting' && 'Reconnecting...'}
            {connectionStatus === 'offline' && 'Offline'}
          </span>
        </div>

        {/* Header */}
        {/* <div className="notifications-sidebar__header">
          <h2 className="notifications-sidebar__title">Notifications</h2>
          <button 
            className="notifications-sidebar__close-btn"
            onClick={onClose}
            aria-label="Close notifications"
          >
            <CustomIcon name="close" size={20} />
          </button>
        </div> */}

        {/* Load Notifications Section */}
        <div className="notifications-sidebar__section notifications-sidebar__section--loads">
          <div className="notifications-sidebar__section-header">
            <div className="notifications-sidebar__section-title-wrapper">
              <CustomIcon name="trucka" size={20} />
              <h3 className="notifications-sidebar__section-title">Loads</h3>
              {sortedLoadNotifications.some(n => !n.read) && (
                <span className="notifications-sidebar__section-badge">
                  {sortedLoadNotifications.filter(n => !n.read).length}
                </span>
              )}
            </div>
          </div>
          {loading && sortedLoadNotifications.length === 0 ? (
            <Loading size="medium" />
          ) : sortedLoadNotifications.length === 0 ? (
            <div className="notifications-sidebar__empty">No load notifications</div>
          ) : (
            <div className="notifications-sidebar__list">
              {sortedLoadNotifications.map((notification, index) => {
                const isUnread = !notification.read;
                const { title, detail } = getNotificationContent(notification);
                const loadStatus = getLoadStatus(notification);
                return (
                  <div 
                    key={notification._id || notification.id} 
                    className={`notifications-sidebar__item ${isUnread ? 'notifications-sidebar__item--unread' : ''} notifications-sidebar__item--load`}
                    style={{ animationDelay: `${0.1 + index * 0.05}s` }}
                    onClick={() => handleNotificationClick(notification)}
                  >
                    <div className="notifications-sidebar__item-icon">
                      <CustomIcon 
                        name={getNotificationIcon(notification.type)} 
                        size={24} 
                      />
                    </div>
                    <div className="notifications-sidebar__item-content">
                      <p className="notifications-sidebar__item-title">
                        {title}
                      </p>
                      {loadStatus && (
                        <div className="notifications-sidebar__load-status">
                          <span className={`status-badge ${getLoadStatusClass(loadStatus)}`}>
                            {loadStatus}
                          </span>
                        </div>
                      )}
                      {detail && (
                        <p className="notifications-sidebar__item-detail">
                          {detail}
                        </p>
                      )}
                      <span className="notifications-sidebar__item-timestamp">
                        {formatTimestamp(notification.createdAt || notification.created_at)}
                      </span>
                    </div>
                    {isUnread && (
                      <div className="notifications-sidebar__item-unread-dot"></div>
                    )}
                    <button
                      className="notifications-sidebar__item-delete"
                      onClick={(e) => {
                        e.stopPropagation();
                        deleteNotification(notification._id || notification.id);
                      }}
                      aria-label="Delete notification"
                      title="Delete notification"
                    >
                      ×
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Payment Notifications Section */}
        <div className="notifications-sidebar__section notifications-sidebar__section--payments">
          <div className="notifications-sidebar__section-header">
            <div className="notifications-sidebar__section-title-wrapper">
              <CustomIcon name="carda" size={20} />
              <h3 className="notifications-sidebar__section-title">Payments</h3>
              {sortedPaymentNotifications.some(n => !n.read) && (
                <span className="notifications-sidebar__section-badge">
                  {sortedPaymentNotifications.filter(n => !n.read).length}
                </span>
              )}
            </div>
          </div>
          {loading && sortedPaymentNotifications.length === 0 ? (
            <Loading size="medium" />
          ) : sortedPaymentNotifications.length === 0 ? (
            <div className="notifications-sidebar__empty">No payment notifications</div>
          ) : (
            <div className="notifications-sidebar__list">
              {sortedPaymentNotifications.map((notification, index) => {
                const isUnread = !notification.read;
                const paymentType = getPaymentType(notification);
                const { title, detail } = getNotificationContent(notification);
                return (
                  <div 
                    key={notification._id || notification.id} 
                    className={`notifications-sidebar__item ${isUnread ? 'notifications-sidebar__item--unread' : ''} notifications-sidebar__item--payment`}
                    style={{ animationDelay: `${0.15 + index * 0.05}s` }}
                    onClick={() => handleNotificationClick(notification)}
                  >
                    <div className="notifications-sidebar__item-icon">
                      <CustomIcon 
                        name={getNotificationIcon(notification.type)} 
                        size={24} 
                      />
                    </div>
                    <div className="notifications-sidebar__item-content">
                      <div className="notifications-sidebar__item-header">
                        <p className="notifications-sidebar__item-title">
                          {title}
                        </p>
                        {paymentType && (
                          <span className={`notifications-sidebar__payment-badge notifications-sidebar__payment-badge--${paymentType}`}>
                            {paymentType === 'receivable' ? 'Receivable' : 'Payable'}
                          </span>
                        )}
                      </div>
                      {detail && (
                        <p className="notifications-sidebar__item-detail">
                          {detail}
                        </p>
                      )}
                      <span className="notifications-sidebar__item-timestamp">
                        {formatTimestamp(notification.createdAt || notification.created_at)}
                      </span>
                    </div>
                    {isUnread && (
                      <div className="notifications-sidebar__item-unread-dot"></div>
                    )}
                    <button
                      className="notifications-sidebar__item-delete"
                      onClick={(e) => {
                        e.stopPropagation();
                        deleteNotification(notification._id || notification.id);
                      }}
                      aria-label="Delete notification"
                      title="Delete notification"
                    >
                      ×
                    </button>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Mark All Read Button - Global */}
        {(sortedLoadNotifications.some(n => !n.read) || 
          sortedPaymentNotifications.some(n => !n.read) || 
          sortedActivityNotifications.some(n => !n.read)) && (
          <div className="notifications-sidebar__mark-all-section">
            <button
              className="notifications-sidebar__mark-all-read"
              onClick={markAllAsRead}
              title="Mark all as read"
            >
              Mark all as read
            </button>
          </div>
        )}

        {/* Activities Section */}
        {/* <div className="notifications-sidebar__section notifications-sidebar__section--activities">
          <div className="notifications-sidebar__section-header">
            <div className="notifications-sidebar__section-title-wrapper">
              <CustomIcon name="activity" size={20} />
              <h3 className="notifications-sidebar__section-title">Activities</h3>
              {sortedActivityNotifications.some(n => !n.read) && (
                <span className="notifications-sidebar__section-badge">
                  {sortedActivityNotifications.filter(n => !n.read).length}
                </span>
              )}
            </div>
          </div>
          {loading && sortedActivityNotifications.length === 0 && displayActivities.length === 0 ? (
            <Loading size="medium" />
          ) : displayActivities.length === 0 ? (
            <div className="notifications-sidebar__empty">No recent activities</div>
          ) : (
            <div className="notifications-sidebar__list">
              {displayActivities.map((activity, index) => {
                const { title, detail } = getNotificationContent(activity);
                return (
                <div 
                  key={activity._id || activity.id} 
                  className="notifications-sidebar__item notifications-sidebar__item--activity"
                  style={{ animationDelay: `${0.35 + index * 0.05}s` }}
                  onClick={() => handleNotificationClick(activity)}
                >
                  <div className="notifications-sidebar__item-avatar">
                    {activity.avatar || activity.user?.profileImage ? (
                      <img src={activity.avatar || activity.user?.profileImage} alt="User" />
                    ) : (
                      <div className="notifications-sidebar__avatar-placeholder">
                        <CustomIcon name="person" size={24} />
                      </div>
                    )}
                  </div>
                  <div className="notifications-sidebar__item-content">
                    <p className="notifications-sidebar__item-title">
                      {title}
                    </p>
                    {detail && (
                      <p className="notifications-sidebar__item-detail">
                        {detail}
                      </p>
                    )}
                    <span className="notifications-sidebar__item-timestamp">
                      {formatTimestamp(activity.createdAt || activity.created_at)}
                    </span>
                  </div>
                </div>
              );})}
            </div>
          )}
        </div> */}
      </div>

      {/* Payment Edit Modal */}
      {paymentEditModal.isOpen && paymentEditModal.paymentData && (
        <PaymentEditModal
          isOpen={paymentEditModal.isOpen}
          onClose={handleClosePaymentModal}
          paymentType={paymentEditModal.paymentType || 'receivable'}
          paymentData={paymentEditModal.paymentData}
          onSuccess={() => {
            handleClosePaymentModal();
            refresh();
          }}
        />
      )}

      {/* Load Info Modal */}
      {loadInfoModal.isOpen && loadInfoModal.loadData && (
        <LoadInfoModal
          isOpen={loadInfoModal.isOpen}
          onClose={handleCloseLoadInfoModal}
          loadData={loadInfoModal.loadData}
        />
      )}

      {/* Info Modal for other notifications - rendered via portal */}
      {infoModal.isOpen && infoModal.notification && createPortal(
        <NotificationInfoModal
          isOpen={infoModal.isOpen}
          notification={infoModal.notification}
          onClose={handleCloseInfoModal}
        />,
        document.body
      )}

      {/* Loading overlay for payment fetch */}
      {loadingPayment && (
        <div className="notifications-sidebar__loading-overlay">
          <Loading size="medium" />
        </div>
      )}

      {/* Loading overlay for load fetch */}
      {loadingLoad && (
        <div className="notifications-sidebar__loading-overlay">
          <Loading size="medium" />
        </div>
      )}
    </div>
  );
};

// Info Modal Component for non-payment notifications
const NotificationInfoModal = ({ isOpen, notification, onClose }) => {
  useEffect(() => {
    if (isOpen) {
      document.body.classList.add('modal-open');
      return () => {
        document.body.classList.remove('modal-open');
      };
    }
  }, [isOpen]);

  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      return () => {
        document.removeEventListener('keydown', handleEscape);
      };
    }
  }, [isOpen, onClose]);

  if (!isOpen || !notification) return null;

  const formatTimestamp = (date) => {
    if (!date) return 'Just now';
    try {
      const dateObj = new Date(date);
      if (isNaN(dateObj.getTime())) return 'Just now';
      return dateObj.toLocaleString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return 'Just now';
    }
  };

  const getNotificationDetails = () => {
    const { type, data } = notification;
    const details = [];

    if (!data) return details;

    switch (type) {
      case 'load.created':
      case 'load_created':
      case 'load.updated':
      case 'load_updated':
      case 'load_assigned': {
        const load = data?.load || data?.entity || {};
        const orderId = load.orderId || data?.orderId || data?.meta?.orderId || 'N/A';
        const loadId = load.id || load._id || data?.entityId || data?.load?.id || data?.load?._id || 'N/A';
        details.push({ label: 'Order ID', value: orderId });
        details.push({ label: 'Load ID', value: loadId });
        details.push({ label: 'Status', value: load.status || data?.status || 'N/A' });
        if (load.customer?.companyName || data?.customer?.companyName) {
          details.push({ label: 'Customer', value: load.customer?.companyName || data.customer?.companyName || 'N/A' });
        }
        if (load.carrier?.companyName || data?.carrier?.companyName) {
          details.push({ label: 'Carrier', value: load.carrier?.companyName || data.carrier?.companyName || 'N/A' });
        }
        if (data?.actor?.email) {
          details.push({ label: 'Created By', value: data.actor.email });
        }
        if (data?.assignedBy) {
          details.push({ label: 'Assigned By', value: data.assignedBy });
        }
        break;
      }
      case 'load.status_updated':
      case 'load_status_update':
      case 'load_status_changed': {
        const load = data?.load || data?.entity || {};
        const orderId = load.orderId || data?.orderId || data?.meta?.orderId || 'N/A';
        const oldStatus = data?.oldStatus || data?.meta?.oldStatus || 'N/A';
        const newStatus = data?.newStatus || data?.meta?.newStatus || load.status || 'N/A';
        details.push({ label: 'Order ID', value: orderId });
        details.push({ label: 'Previous Status', value: oldStatus });
        details.push({ label: 'New Status', value: newStatus });
        if (data?.actor?.email) {
          details.push({ label: 'Updated By', value: data.actor.email });
        }
        break;
      }
      case 'load_delivered':
        if (data?.load) {
          const load = typeof data.load === 'string' ? JSON.parse(data.load) : data.load;
          details.push({ label: 'Order ID', value: load?.orderId || data?.orderId || 'N/A' });
          if (data?.paymentReceivable?.amount) {
            details.push({ label: 'Receivable Amount', value: `$${parseFloat(data.paymentReceivable.amount).toLocaleString()}` });
          }
          if (data?.paymentPayable?.amount) {
            details.push({ label: 'Payable Amount', value: `$${parseFloat(data.paymentPayable.amount).toLocaleString()}` });
          }
        }
        if (data?.updatedBy) {
          details.push({ label: 'Updated By', value: data.updatedBy });
        }
        break;
      case 'payment_receivable_created':
      case 'payment_receivable_status_update':
        details.push({ label: 'Payment Type', value: 'Receivable' });
        details.push({ label: 'Payment ID', value: data?.paymentReceivableId || data?.paymentId || 'N/A' });
        details.push({ label: 'Order ID', value: data?.orderId || data?.load?.orderId || 'N/A' });
        if (data?.amount) {
          details.push({ label: 'Amount', value: `$${parseFloat(data.amount).toLocaleString()}` });
        }
        if (data?.customerRate) {
          details.push({ label: 'Customer Rate', value: `$${parseFloat(data.customerRate).toLocaleString()}` });
        }
        details.push({ label: 'Status', value: data?.newStatus || data?.status || 'N/A' });
        if (data?.oldStatus) {
          details.push({ label: 'Previous Status', value: data.oldStatus });
        }
        if (data?.dueDate) {
          details.push({ label: 'Due Date', value: new Date(data.dueDate).toLocaleDateString() });
        }
        if (data?.invoiceDate) {
          details.push({ label: 'Invoice Date', value: new Date(data.invoiceDate).toLocaleDateString() });
        }
        if (data?.customer?.companyName) {
          details.push({ label: 'Customer', value: data.customer.companyName });
        }
        if (data?.customer?.email) {
          details.push({ label: 'Customer Email', value: data.customer.email });
        }
        break;
      case 'payment_payable_created':
      case 'payment_payable_status_update':
        details.push({ label: 'Payment Type', value: 'Payable' });
        details.push({ label: 'Payment ID', value: data?.paymentPayableId || data?.paymentId || 'N/A' });
        details.push({ label: 'Order ID', value: data?.orderId || data?.load?.orderId || 'N/A' });
        if (data?.amount || data?.grossAmount) {
          details.push({ label: 'Amount', value: `$${parseFloat(data.amount || data.grossAmount).toLocaleString()}` });
        }
        if (data?.netAmount) {
          details.push({ label: 'Net Amount', value: `$${parseFloat(data.netAmount).toLocaleString()}` });
        }
        if (data?.carrierRate) {
          details.push({ label: 'Carrier Rate', value: `$${parseFloat(data.carrierRate).toLocaleString()}` });
        }
        details.push({ label: 'Status', value: data?.newStatus || data?.status || 'N/A' });
        if (data?.oldStatus) {
          details.push({ label: 'Previous Status', value: data.oldStatus });
        }
        if (data?.paymentDate) {
          details.push({ label: 'Payment Date', value: new Date(data.paymentDate).toLocaleDateString() });
        }
        if (data?.carrier?.companyName) {
          details.push({ label: 'Carrier', value: data.carrier.companyName });
        }
        if (data?.carrier?.email) {
          details.push({ label: 'Carrier Email', value: data.carrier.email });
        }
        if (data?.carrier?.mcNumber) {
          details.push({ label: 'MC Number', value: data.carrier.mcNumber });
        }
        if (data?.carrier?.dotNumber) {
          details.push({ label: 'DOT Number', value: data.carrier.dotNumber });
        }
        break;
      case 'payment_status_changed':
        if (data?.payment) {
          const payment = typeof data.payment === 'string' ? JSON.parse(data.payment) : data.payment;
          details.push({ label: 'Payment Type', value: data?.paymentType || payment?.type || 'N/A' });
          details.push({ label: 'Amount', value: payment?.amount ? `$${parseFloat(payment.amount).toLocaleString()}` : 'N/A' });
          details.push({ label: 'Status', value: payment?.status || data?.status || 'N/A' });
        } else {
          details.push({ label: 'Payment Type', value: data?.paymentType || 'N/A' });
          details.push({ label: 'Status', value: data?.newStatus || data?.status || 'N/A' });
        }
        break;
      case 'payment_overdue':
        {
          const isPayable = data?.paymentType === 'payable' || Boolean(data?.paymentPayableId);
          details.push({ label: 'Payment Type', value: isPayable ? 'Payable' : 'Receivable' });
          details.push({ label: 'Payment ID', value: data?.paymentPayableId || data?.paymentReceivableId || data?.paymentId || 'N/A' });
          details.push({ label: 'Order ID', value: data?.orderId || data?.load?.orderId || 'N/A' });
        }
        if (data?.amount) {
          details.push({ label: 'Amount', value: `$${parseFloat(data.amount).toLocaleString()}` });
        }
        if (data?.daysOverdue) {
          details.push({ label: 'Days Overdue', value: `${data.daysOverdue} days` });
        }
        if (data?.dueDate) {
          details.push({ label: 'Due Date', value: new Date(data.dueDate).toLocaleDateString() });
        }
        if (data?.customer?.companyName) {
          details.push({ label: 'Customer', value: data.customer.companyName });
        }
        if (data?.customer?.email) {
          details.push({ label: 'Customer Email', value: data.customer.email });
        }
        if (data?.carrier?.companyName) {
          details.push({ label: 'Carrier', value: data.carrier.companyName });
        }
        if (data?.carrier?.email) {
          details.push({ label: 'Carrier Email', value: data.carrier.email });
        }
        break;
      case 'user.created':
      case 'user_created':
      case 'user.updated':
      case 'user_updated': {
        const user = data?.user || data?.entity || {};
        const name = user.firstName && user.lastName 
          ? `${user.firstName} ${user.lastName}` 
          : user.name || user.username || user.email || 'N/A';
        details.push({ label: 'User Name', value: name });
        details.push({ label: 'Email', value: user.email || data?.email || 'N/A' });
        details.push({ label: 'Role', value: user.role || data?.meta?.role || 'N/A' });
        if (data?.actor?.email) {
          details.push({ label: 'Updated By', value: data.actor.email });
        }
        break;
      }
      case 'carrier.created':
      case 'carrier_created':
      case 'carrier.updated':
      case 'carrier_updated': {
        const carrier = data?.carrier || data?.entity || {};
        details.push({ label: 'Company Name', value: carrier.companyName || data?.meta?.companyName || 'N/A' });
        details.push({ label: 'MC Number', value: carrier.mcNumber || data?.meta?.mcNumber || 'N/A' });
        details.push({ label: 'Email', value: carrier.email || 'N/A' });
        if (data?.actor?.email) {
          details.push({ label: 'Updated By', value: data.actor.email });
        }
        break;
      }
      case 'customer.created':
      case 'customer_created':
      case 'customer.updated':
      case 'customer_updated': {
        const customer = data?.customer || data?.entity || {};
        details.push({ label: 'Company Name', value: customer.companyName || data?.meta?.companyName || 'N/A' });
        details.push({ label: 'Email', value: customer.email || 'N/A' });
        if (data?.actor?.email) {
          details.push({ label: 'Updated By', value: data.actor.email });
        }
        break;
      }
      default:
        // Generic data display - handle both objects and strings
        Object.keys(data).forEach(key => {
          if (key === 'load' || key === 'user' || key === 'payment' || key === 'customer' || key === 'carrier') {
            // Skip nested objects, they're handled above
            return;
          }
          const value = data[key];
          if (value !== null && value !== undefined) {
            if (typeof value === 'object' && !Array.isArray(value)) {
              // For nested objects, show as JSON
              details.push({ label: key.charAt(0).toUpperCase() + key.slice(1), value: JSON.stringify(value, null, 2) });
            } else if (Array.isArray(value)) {
              details.push({ label: key.charAt(0).toUpperCase() + key.slice(1), value: value.join(', ') });
            } else {
              details.push({ label: key.charAt(0).toUpperCase() + key.slice(1), value: String(value) });
            }
          }
        });
    }

    return details;
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div className="notification-info-modal-overlay" onClick={handleOverlayClick}>
      <div className="notification-info-modal" onClick={(e) => e.stopPropagation()}>
        <div className="notification-info-modal__header">
          <h2 className="notification-info-modal__title">Notification Details</h2>
          <button
            className="notification-info-modal__close-btn"
            onClick={onClose}
            aria-label="Close modal"
          >
            <CustomIcon name="close" size={20} />
          </button>
        </div>

        <div className="notification-info-modal__body">
          {notification.title && (
            <div className="notification-info-modal__section">
              <h3 className="notification-info-modal__section-title">Title</h3>
              <p className="notification-info-modal__message">
                {notification.title}
              </p>
            </div>
          )}
          <div className="notification-info-modal__section">
            <h3 className="notification-info-modal__section-title">Message</h3>
            <p className="notification-info-modal__message">
              {notification.message || notification.title || 'No message available'}
            </p>
          </div>

          <div className="notification-info-modal__section">
            <h3 className="notification-info-modal__section-title">Type</h3>
            <p className="notification-info-modal__type">
              {notification.type?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) || 'Unknown'}
            </p>
          </div>

          <div className="notification-info-modal__section">
            <h3 className="notification-info-modal__section-title">Timestamp</h3>
            <p className="notification-info-modal__timestamp">
              {formatTimestamp(notification.createdAt || notification.created_at)}
            </p>
          </div>

          {getNotificationDetails().length > 0 && (
            <div className="notification-info-modal__section">
              <h3 className="notification-info-modal__section-title">Details</h3>
              <div className="notification-info-modal__details">
                {getNotificationDetails().map((detail, index) => (
                  <div key={index} className="notification-info-modal__detail-item">
                    <span className="notification-info-modal__detail-label">{detail.label}:</span>
                    <span className="notification-info-modal__detail-value">{detail.value}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {notification.data && Object.keys(notification.data).length > 0 && (
            <div className="notification-info-modal__section">
              <h3 className="notification-info-modal__section-title">Raw Data</h3>
              <pre className="notification-info-modal__raw-data">
                {JSON.stringify(notification.data, null, 2)}
              </pre>
            </div>
          )}
        </div>

        <div className="notification-info-modal__footer">
          <button
            className="notification-info-modal__close-button"
            onClick={onClose}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default NotificationsSidebar;
